const PICKER_SCRIPT_ID = 'google-picker-api-script';
const PICKER_API_SRC = 'https://apis.google.com/js/api.js';

/**
 * Cached load of the Google Picker API. Shared across concurrent callers so the
 * `api.js` script and the `gapi.load('picker')` step run at most once. Reset to
 * null on failure so a later call can retry.
 */
let pickerApiPromise: Promise<void> | null = null;

interface OpenDrivePickerOptions {
  /** Browser API key for the Picker (VITE_APP_GOOGLE_API_KEY). */
  apiKey: string;
  /** Google Cloud project number used as the Picker app id. */
  appId: string;
  /** Short-lived OAuth access token authorizing the Picker. */
  accessToken: string;
  /** Which Drive views to show; one Picker View is added per entry. */
  viewIds: Array<'SPREADSHEETS' | 'DOCUMENTS' | 'PRESENTATIONS'>;
}

/**
 * Load the Google Picker API, injecting the `api.js` script once and then
 * loading the `picker` module via `gapi.load`.
 *
 * @returns {Promise<void>} Resolves once `window.google.picker` is usable.
 */
function loadPickerApi(): Promise<void> {
  if (window.google?.picker) {
    return Promise.resolve();
  }
  if (pickerApiPromise) {
    return pickerApiPromise;
  }

  pickerApiPromise = new Promise<void>((resolve, reject) => {
    const failLoad = (error: Error) => {
      pickerApiPromise = null;
      document.getElementById(PICKER_SCRIPT_ID)?.remove();
      reject(error);
    };

    const script = document.createElement('script');
    script.id = PICKER_SCRIPT_ID;
    script.src = PICKER_API_SRC;
    script.async = true;
    script.onload = () => {
      if (!window.gapi) {
        failLoad(new Error('Google API script loaded without exposing gapi'));
        return;
      }
      window.gapi.load('picker', {
        callback: () => resolve(),
        onerror: () => failLoad(new Error('Failed to load the Google Picker module')),
      });
    };
    script.onerror = () => failLoad(new Error('Failed to load the Google API script'));
    document.head.appendChild(script);
  });

  return pickerApiPromise;
}

/**
 * Open the Google Picker scoped to the requested Drive views (spreadsheets,
 * documents, presentations, or any combination) and resolve the picked file id.
 *
 * @param {OpenDrivePickerOptions} options - API key, app id, OAuth token, and
 *   which Drive views to show.
 * @returns {Promise<string | null>} The picked file id, or null if the user
 *   cancels the picker.
 */
async function openDrivePicker(options: OpenDrivePickerOptions): Promise<string | null> {
  await loadPickerApi();

  const pickerApi = window.google?.picker;
  if (!pickerApi) {
    throw new Error('Google Picker API is unavailable after loading');
  }

  return new Promise<string | null>((resolve) => {
    let settled = false;
    const settle = (fileId: string | null) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(fileId);
    };

    const builder = new pickerApi.PickerBuilder()
      .setDeveloperKey(options.apiKey)
      .setAppId(options.appId)
      .setOAuthToken(options.accessToken);

    for (const viewId of options.viewIds) {
      builder.addView(new pickerApi.View(pickerApi.ViewId[viewId]));
    }

    const picker = builder
      .setOrigin(window.location.origin)
      .setCallback((data) => {
        const action = data[pickerApi.Response.ACTION];
        if (action === pickerApi.Action.PICKED) {
          const documents = data[pickerApi.Response.DOCUMENTS] as Array<Record<string, unknown>> | undefined;
          const fileId = documents?.[0]?.[pickerApi.Document.ID];
          settle(typeof fileId === 'string' ? fileId : null);
        } else if (action === pickerApi.Action.CANCEL) {
          settle(null);
        }
      })
      .build();

    picker.setVisible(true);
  });
}

export { loadPickerApi, openDrivePicker };
export type { OpenDrivePickerOptions };
